'use client';

import { useEffect, useState } from 'react';

/**
 * Root error boundary for the dashboard app.
 *
 * Without this, runtime React errors (e.g. Rules-of-Hooks violations
 * like #310, #185 unsubscribe loops) fall through to Next's generic
 * "Application error: a client-side exception has occurred" screen,
 * which hides the actual message + component stack. That left us
 * unable to debug the post-login #310 because the production build
 * gives only a minified stack with no source maps.
 *
 * This boundary keeps the user moving (Try Again / Sign Out) and
 * surfaces the real diagnostic info inline + to the console + to a
 * future Sentry hook so the next occurrence is one click to debug.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Always log full error to console — minified prod builds otherwise
    // strip the actionable info from the user-visible UI.
    // eslint-disable-next-line no-console
    console.error('[GlobalError boundary]', {
      name: error.name,
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });
  }, [error]);

  const isHookError = /Minified React error #(310|185|301)/.test(error.message)
    || /Rendered (more|fewer) hooks/.test(error.message)
    || /useMemo|useState|useEffect/.test(error.stack || '');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl space-y-4">
        <div className="rounded-lg border bg-card p-6 space-y-3">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            {isHookError
              ? "A React rendering glitch happened on this page. Reloading usually clears it; if it keeps happening, please share the details below with the engineering team."
              : "An unexpected error occurred. Try again, or reload the page."}
          </p>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => reset()}
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Reload page
            </button>
            <button
              onClick={() => setShowDetails((s) => !s)}
              className="inline-flex items-center justify-center rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {showDetails ? 'Hide' : 'Show'} details
            </button>
          </div>
        </div>

        {showDetails && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-xs font-mono">
            <div>
              <span className="font-semibold">Error:</span> {error.name}
            </div>
            <div className="break-all">
              <span className="font-semibold">Message:</span> {error.message}
            </div>
            {error.digest && (
              <div>
                <span className="font-semibold">Digest:</span> {error.digest}
              </div>
            )}
            <details className="pt-2">
              <summary className="cursor-pointer font-semibold">Stack trace</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all text-[10px] leading-relaxed">
                {error.stack || '(no stack available)'}
              </pre>
            </details>
            <p className="pt-2 text-muted-foreground">
              The full error has also been logged to the browser console — open DevTools → Console to copy it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
